import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import classnames from "classnames";
import {
  Card,
  CardBody,
  CardFooter,
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DebounceInput } from "react-debounce-input";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { ACTIONS, useJobs } from "../../providers/jobsProvider";
import { faList, faThLarge } from "@fortawesome/free-solid-svg-icons";
import { jobsApi } from "../../services/jobServices";
import { utils } from "../../utils/utils";
import AdvanceTableWrapper from "../../components/advanceTable/AdvanceTableWrapper";
import AdvanceTable from "../../components/advanceTable/AdvanceTable";
import AdvanceTablePagination from "../../components/advanceTable/AdvanceTablePagination";
import LaborCodeChart from "../../components/admin/LaborCodeChart";
import FabricationHoursChart from "../../components/admin/FabricationHoursChart";
import Header from "../../components/Header";
import HeaderTitle from "../../components/HeaderTitle";
import JobSchedulingCalendar from "../../components/admin/production/JobSchedulingCalendar";

const tabsConfig = [
  {
    id: "IN_PM_TAB",
    title: "In PM",
    status: 10,
    chartComponent: LaborCodeChart,
    useWrapperCard: true,
  },
  {
    id: "IN_PROGRESS_TAB",
    title: "In Progress",
    status: 4,
    chartComponent: FabricationHoursChart,
    useWrapperCard: false,
  },
];

const columns = [
  {
    id: "jobNumber",
    accessor: "jobNumber",
    Header: "Job#",
    headerProps: { className: "text-truncate" },
    width: 100,
    Cell: (rowData) => {
      const { jobNumber } = rowData.row.original;
      return jobNumber || "-";
    },
  },
  {
    id: "customer",
    accessor: "customer",
    disableSortBy: true,
    Header: "Customer Name",
    headerProps: { className: "text-truncate" },
    width: 250,
    Cell: (rowData) => {
      const { customer } = rowData.row.original;
      return customer ? `${customer?.name}` : "-";
    },
  },
  {
    id: "sequenceName",
    accessor: "sequenceName",
    disableSortBy: true,
    Header: "Sequence Name",
    headerProps: { className: "text-truncate" },
    width: 190,
    Cell: (rowData) => {
      const { sequence } = rowData.row.original;
      return sequence.length > 0
        ? sequence[sequence.length - 1].description
        : "-";
    },
  },
  {
    id: "description",
    accessor: "description",
    disableSortBy: true,
    Header: "Description",
    headerProps: { className: "text-truncate" },
    width: 250,
    Cell: (rowData) => {
      const { description } = rowData.row.original;
      return description || "-";
    },
  },
  {
    id: "contractAmount",
    accessor: "contractAmount",
    Header: "Contract Amount",
    disableSortBy: true,
    headerProps: { className: "text-truncate" },
    width: 150,
    Cell: (rowData) => {
      const { contractAmount } = rowData.row.original;
      return contractAmount ? `${utils.formatCurrency(contractAmount)}` : "-";
    },
  },
  {
    id: "contractDate",
    accessor: "contractDate",
    disableSortBy: true,
    Header: "Contract Date",
    headerProps: { className: "text-truncate" },
    width: 150,
    Cell: (rowData) => {
      const { contractDate } = rowData.row.original;
      return contractDate ? utils.formatDate(contractDate) : "-";
    },
  },
  {
    id: "totalManHours",
    accessor: "totalManHours",
    Header: "Total MH",
    headerProps: { className: "text-truncate" },
    width: 130,
    Cell: (rowData) => {
      return `${utils.getManHours(rowData.row.original)} hours`;
    },
  },
  {
    id: "estShipDate",
    accessor: "estimatedShipDate",
    Header: "Est. Ship Date",
    headerProps: { className: "text-truncate" },
    width: 130,
    Cell: (rowData) => {
      const { estimatedShipDate } = rowData.row.original;
      return estimatedShipDate ? utils.formatDate(estimatedShipDate) : "-";
    },
  },
  {
    id: "sequenceShip",
    accessor: (row) => row.scheduling?.sequenceShipDate,
    Header: "Sequence Ship",
    headerProps: { className: "text-truncate" },
    width: 130,
    Cell: (rowData) => {
      const date = rowData.row.original.scheduling?.sequenceShipDate;
      return date ? utils.formatDate(date) : "-";
    },
  },
  {
    id: "processedKittedNeed",
    accessor: (row) => row.scheduling?.processedKittedNeedDate,
    Header: "Processed / Kitted Need",
    headerProps: { className: "text-truncate" },
    width: 130,
    Cell: (rowData) => {
      const date = rowData.row.original.scheduling?.processedKittedNeedDate;
      return date ? utils.formatDate(date) : "-";
    },
  },
  {
    id: "backFromOP",
    accessor: (row) => row.scheduling?.backFromOpDate,
    Header: "Back from OP",
    headerProps: { className: "text-truncate" },
    width: 130,
    Cell: (rowData) => {
      const date = rowData.row.original.scheduling?.backFromOpDate;
      return date ? utils.formatDate(date) : "-";
    },
  },
  {
    id: "transmittalNeed",
    accessor: (row) => row.scheduling?.transmittalNeedDate,
    Header: "Transmittal Need",
    headerProps: { className: "text-truncate" },
    width: 130,
    Cell: (rowData) => {
      const date = rowData.row.original.scheduling?.transmittalNeedDate;
      return date ? utils.formatDate(date) : "-";
    },
  },
  {
    id: "fabComplete",
    accessor: (row) => row.scheduling?.fabCompleteDate,
    Header: "Fab Complete",
    headerProps: { className: "text-truncate" },
    width: 130,
    Cell: (rowData) => {
      const date = rowData.row.original.scheduling?.fabCompleteDate;
      return date ? utils.formatDate(date) : "-";
    },
  },
];

const Production = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [jobsContext, setJobsContext] = useJobs();
  const [jobs, setJobs] = useState({ data: [], count: 0, totalPages: 0 });
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);
  const [sortBy, setSortBy] = useState("id");
  const [direction, setDirection] = useState("desc");
  const [selectedTab, setSelectedTab] = useState(
    location.state?.tab || tabsConfig[0].id,
  );
  const [viewMode, setViewMode] = useState("list"); // 'list' or 'calendar'

  const onSort = useCallback(
    ([data]) => {
      if (data) {
        const newSortBy = data.id;
        const newDirection = data.desc ? "desc" : "asc";
        if (
          !newSortBy ||
          (sortBy === newSortBy && direction === newDirection)
        ) {
          return;
        }
        setSortBy(newSortBy);
        setDirection(newDirection);
      } else {
        setSortBy(null);
        setDirection(null);
      }
    },
    [sortBy, direction],
  );

  const handleTabChange = (tab) => {
    if (selectedTab !== tab) {
      setSelectedTab(tab);
      setPage(1);
      setJobs({ data: [], count: 0, totalPages: 0 });
    }
  };

  const details = (jobId) => {
    navigate(`/production/${jobId}`);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const currentTab = tabsConfig.find((tab) => tab.id === selectedTab);
        if (!currentTab) return;

        const jobsData = await jobsApi.getJobs({
          search: jobsContext.search || "",
          page: page - 1,
          pageSize: sizePerPage,
          sortBy: sortBy,
          direction: direction,
          statusId: [currentTab.status],
          estimatorId: null,
          managerId: null,
          customerId: null,
          currentYear: null,
          pmApproval: false,
          execApproval: false,
          scheduling: true,
        });
        setJobs(jobsData);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Error fetching jobs:", error);
      }
    };
    fetchData();
  }, [sortBy, direction, sizePerPage, page, selectedTab, jobsContext.search]);

  const renderTabContent = () => {
    const currentTabConfig = tabsConfig.find((tab) => tab.id === selectedTab);
    const ChartComponent = currentTabConfig?.chartComponent;
    const useWrapperCard = currentTabConfig?.useWrapperCard;

    const renderPagination = (
      <AdvanceTablePagination
        rowsPerPageOptions={[5, 10, 15, 25, 50, 75, 100, 250]}
        totalCount={jobs?.count}
        pageCount={jobs?.totalPages}
        currentPage={page - 1}
        onPageChange={(newPage) => setPage(newPage)}
        pageSize={sizePerPage}
        onPageSizeChange={(newSizePerPage) => {
          setSizePerPage(newSizePerPage);
          setPage(1);
        }}
      />
    );

    if (viewMode === "calendar") {
      return (
        <Row>
          <Col xs="12">
            <JobSchedulingCalendar
              jobs={jobs?.data || []}
              isLoading={loading}
            />
            <Card className="col-12 px-0 mt-0 shadow-none">
              <CardFooter className="bg-white border-0 px-3">
                {renderPagination}
              </CardFooter>
            </Card>
          </Col>
        </Row>
      );
    }

    return (
      <>
        {ChartComponent && (
          <Row>
            <Col xs="12">
              {useWrapperCard ? (
                <Card className="pb-2 mb-0">
                  <CardBody className="pb-2">
                    <ChartComponent />
                  </CardBody>
                </Card>
              ) : (
                <ChartComponent />
              )}
            </Col>
          </Row>
        )}
        <Row>
          <Col xs="12">
            <div className="w-100">
              <AdvanceTableWrapper
                columns={columns}
                data={jobs?.data || []}
                pageSize={sizePerPage}
                sortable
                onSort={onSort}
                defaultSort={{
                  sortBy: sortBy,
                  direction: direction,
                }}
              >
                <Card className="col-12 px-0 overflow-x-auto">
                  <CardBody>
                    <AdvanceTable
                      onRowClick={(row) => details(row.original.id)}
                      table
                      isLoading={loading}
                      headerClassName="text-muted small"
                      tableProps={{
                        striped: true,
                        className: "mb-0",
                      }}
                    />
                  </CardBody>
                  <CardFooter>{renderPagination}</CardFooter>
                </Card>
              </AdvanceTableWrapper>
            </div>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <Container fluid>
      <Header className="mb-3 ">
        <HeaderTitle className="d-flex flex-column align-items-start">
          <div className="d-flex justify-content-between w-100">
            <span className="col-12 px-0 col-lg-6 text-dark">
              Production Scheduling
            </span>
            <div className="d-flex align-items-center bg-light rounded p-0 px-1 mb-1">
              <Button
                size="md"
                color="link"
                className={classnames("p-1 box-shadow-none", {
                  "text-primary": viewMode === "list",
                  "text-muted": viewMode !== "list",
                })}
                onClick={() => setViewMode("list")}
              >
                <FontAwesomeIcon icon={faList} />
              </Button>
              <div
                className="border-left mx-1 border-secondary opacity-50"
                style={{ height: "15px" }}
              />
              <Button
                size="md"
                color="link"
                className={classnames("p-1 box-shadow-none", {
                  "text-primary": viewMode === "calendar",
                  "text-muted": viewMode !== "calendar",
                })}
                onClick={() => setViewMode("calendar")}
              >
                <FontAwesomeIcon icon={faThLarge} />
              </Button>
            </div>
          </div>
        </HeaderTitle>
      </Header>
      <Row>
        <Col md="9" className="flex-grow-1">
          <Nav
            tabs
            className="d-flex text-center flex-nowrap job-details-nav mt-0"
          >
            <div className="d-flex flex-grow-1">
              {tabsConfig.map((tab) => (
                <NavItem key={tab.id} className="cursor-pointer">
                  <NavLink
                    className={classnames({
                      active: selectedTab === tab.id,
                    })}
                    onClick={() => handleTabChange(tab.id)}
                  >
                    {tab.title}
                  </NavLink>
                </NavItem>
              ))}
            </div>
          </Nav>
        </Col>
        <Col md="3" className="flex-grow-1">
          <InputGroup size="m" className="mr-3">
            <DebounceInput
              minLength={2}
              debounceTimeout={500}
              className="form-control"
              placeholder="Search for.."
              value={jobsContext.search}
              onChange={(evt) =>
                setJobsContext({
                  action: ACTIONS.SEARCH,
                  payload: { search: evt.target.value },
                })
              }
            />
            <InputGroupText className="search-jobs input-group-text bg-primary text-white border-left-0 border-primary">
              <FontAwesomeIcon icon={faSearch} />
            </InputGroupText>
          </InputGroup>
        </Col>
      </Row>
      <TabContent activeTab={selectedTab}>
        {tabsConfig.map((tab) => (
          <TabPane key={tab.id} tabId={tab.id}>
            {selectedTab === tab.id && renderTabContent()}
          </TabPane>
        ))}
      </TabContent>
    </Container>
  );
};

export default Production;
